import React, { useState } from "react";
import {
    apiConstants,
    postMultipart,
} from "../common/constants";
import {
    FormProvider,
    RHFFileField,
    RHFSearchableSelectField,
} from "../common/hook-form";
import { useForm } from "react-hook-form";
import {
    Button,
    Grid,
    Card,
    CardContent,
    Divider,
    Chip,
    Stack,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { useSnackbar } from "notistack";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PageTitle from "../components/PageTitle";
import DownloadSampleFile from "../components/DownloadSampleFile";
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from "../redux/commonApiCalls";

// Two modes for the sorter-image lookup:
//  - MERCHANT: existing API, requires a merchant selection (tools/getBulkSorterImages)
//  - AWB: admin API, no merchant needed - matches AWBs across all merchants (tools/getBulkSorterImagesAdmin)
const MODE = { MERCHANT: 'merchant', AWB: 'awb' };

const SorterImage = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const [excelLink, setExcelLink] = useState(null);
    const [mode, setMode] = useState(MODE.MERCHANT);
    const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()

    const methodsBulkUpload = useForm({
        shouldUnregister: true,
        defaultValues: {
            file: null,
            merchant: ''
        }
    });

    const { handleSubmit, reset } = methodsBulkUpload;

    const handleModeChange = (newMode) => {
        if (newMode === mode || isUploadingFile) return;
        setMode(newMode);
        reset({ file: null, merchant: '' });
        setExcelLink(null);
    };

    const onSubmit = async (data) => {
        setIsUploadingFile(true);
        try {
            const formData = new FormData();
            formData.append("excelFile", data.file[0]);

            let endpoint;
            if (mode === MODE.MERCHANT) {
                formData.append("merchantOid", data.merchant);
                endpoint = apiConstants.SORTER_IMAGE_DOWNLOAD;
            } else {
                endpoint = apiConstants.SORTER_IMAGE_DOWNLOAD_BY_AWB;
            }

            const response = await postMultipart(`${endpoint}`, formData);
            if (response.status === 200 && response?.data?.excel) {
                setExcelLink(response?.data?.excel);
                enqueueSnackbar("Successfully Uploaded!", { variant: "success" });
            } else {
                enqueueSnackbar(response.data, { variant: "error" });
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error.message;
            enqueueSnackbar(errorMessage || "Error creating order", {
                variant: "error"
            });
        } finally {
            setIsUploadingFile(false);
        }
    };


    return (
        <>
            <PageTitle>Sorter Image Download</PageTitle>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                <Chip
                                    label="By Merchant"
                                    clickable
                                    disabled={isUploadingFile}
                                    color={mode === MODE.MERCHANT ? "primary" : "default"}
                                    variant={mode === MODE.MERCHANT ? "filled" : "outlined"}
                                    onClick={() => handleModeChange(MODE.MERCHANT)}
                                />
                                <Chip
                                    label="By AWB"
                                    clickable
                                    disabled={isUploadingFile}
                                    color={mode === MODE.AWB ? "primary" : "default"}
                                    variant={mode === MODE.AWB ? "filled" : "outlined"}
                                    onClick={() => handleModeChange(MODE.AWB)}
                                />
                            </Stack>
                            <FormProvider
                                methods={methodsBulkUpload}
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <Grid container spacing={2}>
                                    {mode === MODE.MERCHANT && (
                                        <Grid item xs={12} sm={12}>
                                            <RHFSearchableSelectField
                                                fullWidth
                                                name="merchant"
                                                label={`Select Merchant`}
                                                required={true}
                                                options={
                                                    merchants.map((val) => ({
                                                        value: val.id,
                                                        label: val.name,
                                                    }))
                                                }
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={12} sm={12}>
                                        <RHFFileField name="file" fileType={["xlsx"]} required={true} />
                                    </Grid>
                                    <Grid item xs={10}>
                                        <Grid container spacing={1} alignItems="center">
                                            <Grid item>
                                                <LoadingButton
                                                    startIcon={<FileUploadIcon />}
                                                    type="submit"
                                                    loading={isUploadingFile}
                                                    variant="contained"
                                                    color="warning"
                                                    size="small"
                                                >
                                                    Upload
                                                </LoadingButton>
                                            </Grid>
                                            <Grid item>
                                                <DownloadSampleFile
                                                    fileName={"BULK_SORTER_IMAGE_DOWNLOAD_BY_AWB"}
                                                    label='SAMPLE'
                                                    excel={'excel'}
                                                />
                                            </Grid>
                                            <Grid item>
                                                <Button
                                                    onClick={() => { reset(); setExcelLink(null) }}
                                                    variant="contained"
                                                    color="error"
                                                    size="small"
                                                >
                                                    Reset
                                                </Button>
                                            </Grid>
                                            {excelLink && (
                                                <>
                                                    <Grid item sx={{ mx: 2 }}>
                                                        <Divider orientation="vertical" flexItem sx={{ height: '30px' }} />
                                                    </Grid>
                                                    <Grid item>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            size="small"
                                                            href={excelLink}
                                                            target="_blank"
                                                            startIcon={<FileDownloadIcon />}
                                                        >
                                                            Download Excel
                                                        </Button>
                                                    </Grid>
                                                </>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </FormProvider>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

        </>

    );
};
export default SorterImage
